import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Lock, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
const CAROUSEL_IMAGES = [{
  src: 'https://cdn.magicpatterns.com/uploads/cVxZaj5iHimcpWQSCzfF4h/CARRUSEL_FOTO_1.png',
  alt: 'Mujer realizando ritual de prosperidad con baño de pies'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/c8bAiuF2pvPvDtfjaMxNWz/CARRUSEL_FOTO_2.png',
  alt: 'Ritual de descanso con baño de pies y velas'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/rFYL2aixWRKzKcEjL5TGRX/CARRUSEL_FOTO_3.png',
  alt: 'Ritual de bienestar con baño de pies y hierbas naturales'
}];
export function Hero() {
  const [currentImage, setCurrentImage] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % CAROUSEL_IMAGES.length);
    }, 4500);
    return () => clearInterval(interval);
  }, []);
  // Warm-up: prefetch slides 2 & 3 once the page is idle so rotation feels instant
  useEffect(() => {
    const idle = (window as any).requestIdleCallback || ((cb: () => void) => setTimeout(cb, 1500));
    const handle = idle(() => {
      CAROUSEL_IMAGES.slice(1).forEach(img => {
        const i = new Image();
        i.decoding = 'async';
        i.src = img.src;
      });
    });
    return () => {
      if ((window as any).cancelIdleCallback && typeof handle === 'number') {
        ;
        (window as any).cancelIdleCallback(handle);
      }
    };
  }, []);
  const goToImage = (i: number) => setCurrentImage(i);
  const next = () => setCurrentImage(prev => (prev + 1) % CAROUSEL_IMAGES.length);
  const prev = () => setCurrentImage(p => (p - 1 + CAROUSEL_IMAGES.length) % CAROUSEL_IMAGES.length);
  return <section className="relative bg-[#2F4538] text-[#F5F0E8] overflow-hidden" data-id="element-184">
      <div className="absolute inset-0 opacity-20" aria-hidden="true" data-id="element-185">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#B4A7D6] rounded-full blur-[120px]" data-id="element-186"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D4AF37] rounded-full blur-[120px] opacity-40" data-id="element-187"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20" data-id="element-188">
        {/* Top: title + bundle image */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="text-center max-w-4xl mx-auto mb-12 sm:mb-16" data-id="element-189">
          <div className="flex items-center justify-center gap-2 mb-6" data-id="element-190">
            <Sparkles className="w-5 h-5 text-[#B4A7D6]" data-id="element-191" />
            <span className="text-[#D4AF37] font-medium text-sm uppercase tracking-[0.2em]" data-id="element-192">
              RITUALES DE PIES ANCESTRALES
            </span>
            <Sparkles className="w-5 h-5 text-[#B4A7D6]" data-id="element-193" />
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight" data-id="element-194">
            El Sistema Completo de{' '}
            <span className="text-[#B4A7D6]" data-id="element-195">
              Transformación Energética para
            </span>{' '}
            Mujeres que Necesitan{' '}
            <span className="text-[#B4A7D6]" data-id="element-196">Desconectar del Caos</span>
          </h1>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          delay: 0.3
        }} className="relative max-w-3xl mx-auto" data-id="element-197">
            <div className="absolute -inset-4 bg-gradient-to-r from-[#B4A7D6]/20 via-[#D4AF37]/20 to-[#B4A7D6]/20 rounded-3xl blur-2xl" aria-hidden="true" data-id="element-198"></div>
            <img src="https://cdn.magicpatterns.com/uploads/2Uy1ouDYEkv5CsNPZdVTGV/BUNDLE_HORIZONTAL.png" alt="Bundle completo Rituales de Pies Ancestrales" width={1200} height={675} loading="eager" decoding="sync" fetchPriority="high" className="relative rounded-2xl w-full h-auto shadow-2xl" data-id="element-199" />
          </motion.div>
        </motion.div>

        {/* Bottom: copy + carousel */}
        <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-200">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} data-id="element-201">
            <p className="text-lg sm:text-xl text-[#F5F0E8]/90 mb-8 leading-relaxed font-light" data-id="element-202">
              Más de 100 rituales personalizados para limpiar tu energía, dormir
              como hace años no lo hacés, reducir la ansiedad y atraer
              prosperidad...{' '}
              <strong className="font-bold text-[#1a1a1a] relative inline-block px-2 py-0.5 rounded-md highlight-pulse" data-id="element-203">
                en solo 7 días
              </strong>
              <span className="font-semibold text-[#D4AF37]" data-id="element-204">
                {' '}
                , con ingredientes que ya tenés en tu cocina.
              </span>
            </p>

            <div className="space-y-4 mb-10" data-id="element-205">
              {[{
              emoji: '💧',
              text: 'Fácil de preparar (5-10 minutos)'
            }, {
              emoji: '🌿',
              text: 'Ingredientes accesibles (todo en tu barrio)'
            }, {
              emoji: '🔮',
              text: 'Resultados desde la primera vez'
            }, {
              emoji: '🇦🇷',
              text: 'Adaptado para Argentina (ingredientes, energía, calendario lunar local)'
            }].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-206">
                  <span className="text-xl shrink-0 mt-0.5" aria-hidden="true" data-id="element-207">
                    {item.emoji}
                  </span>
                  <span className="text-base sm:text-lg text-[#F5F0E8]/90" data-id="element-208">
                    {item.text}
                  </span>
                </div>)}
            </div>

            <div className="flex flex-col items-start gap-6" data-id="element-209">
              <a href="#oferta" className="inline-block bg-[#B4A7D6] text-[#F5F0E8] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#9D8FC4] transition-all transform hover:scale-105 shadow-[0_10px_30px_rgba(180,167,214,0.4)] text-center w-full sm:w-auto" data-id="element-210">
                QUIERO TRANSFORMAR MI ENERGÍA AHORA →
              </a>

              <div className="text-center sm:text-left w-full sm:w-auto bg-black/20 p-4 rounded-2xl border border-white/5" data-id="element-211">
                <p className="text-sm text-[#D4AF37] font-medium uppercase tracking-wider mb-2 flex items-center gap-2 justify-center sm:justify-start" data-id="element-212">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" data-id="element-213"></span>
                  Precio de lanzamiento por tiempo limitado
                </p>
                <div className="flex items-center justify-center sm:justify-start gap-3" data-id="element-214">
                  <span className="text-lg text-[#F5F0E8]/50 line-through" data-id="element-215">
                    Antes: $96.000
                  </span>
                  <span className="text-2xl font-bold text-[#F5F0E8]" data-id="element-216">
                    HOY: $15.990
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-[#F5F0E8]/70" data-id="element-217">
                <div className="flex items-center gap-1.5" data-id="element-218">
                  <Lock className="w-4 h-4 text-[#D4AF37]" data-id="element-219" />
                  <span data-id="element-220">Pago seguro con Mercado Pago</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-221">
                  <Mail className="w-4 h-4 text-[#D4AF37]" data-id="element-222" />
                  <span data-id="element-223">Acceso inmediato por email</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Carousel */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.5
        }} className="relative" data-id="element-224">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/5] sm:aspect-square lg:aspect-[4/5] border border-[#D4AF37]/20 bg-[#2F4538]" data-id="element-225">
              <AnimatePresence mode="wait" data-id="element-226">
                <motion.img key={currentImage} src={CAROUSEL_IMAGES[currentImage].src} alt={CAROUSEL_IMAGES[currentImage].alt} width={800} height={1000} loading={currentImage === 0 ? 'eager' : 'lazy'} decoding={currentImage === 0 ? 'sync' : 'async'} fetchPriority={currentImage === 0 ? 'high' : 'low'} initial={{
                opacity: 0,
                scale: 1.05
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 1.2
              }} className="absolute inset-0 w-full h-full object-cover" data-id="element-227" />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-[#2F4538] via-[#2F4538]/10 to-transparent pointer-events-none" aria-hidden="true" data-id="element-228"></div>

              {/* Arrows */}
              <button onClick={prev} aria-label="Imagen anterior" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/50 transition-colors flex items-center justify-center text-[#F5F0E8] z-10" data-id="element-229">
                <ChevronLeft className="w-5 h-5" data-id="element-230" />
              </button>
              <button onClick={next} aria-label="Siguiente imagen" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/50 transition-colors flex items-center justify-center text-[#F5F0E8] z-10" data-id="element-231">
                <ChevronRight className="w-5 h-5" data-id="element-232" />
              </button>

              {/* Dots */}
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-10" data-id="element-233">
                {CAROUSEL_IMAGES.map((_, i) => <button key={i} onClick={() => goToImage(i)} aria-label={`Ir a imagen ${i + 1}`} className={`h-2 rounded-full transition-all ${currentImage === i ? 'w-8 bg-[#D4AF37]' : 'w-2 bg-white/50 hover:bg-white/80'}`} data-id="element-234" />)}
              </div>
            </div>

            <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#B4A7D6] rounded-full blur-3xl opacity-40 -z-10" aria-hidden="true" data-id="element-235"></div>
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#D4AF37] rounded-full blur-3xl opacity-20 -z-10" aria-hidden="true" data-id="element-236"></div>

            {/* Featured quote below carousel */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.9
          }} className="relative mt-8 sm:mt-10" data-id="element-237">
              <div className="absolute -inset-3 bg-gradient-to-r from-[#B4A7D6]/30 via-[#B4A7D6]/10 to-[#B4A7D6]/30 rounded-3xl blur-2xl" aria-hidden="true" data-id="element-238"></div>
              <div className="relative bg-gradient-to-br from-[#B4A7D6]/15 via-white/5 to-[#B4A7D6]/15 backdrop-blur-sm border-l-4 border-[#B4A7D6] rounded-2xl px-6 py-5 sm:px-7 sm:py-6 shadow-lg" data-id="element-239">
                <span className="absolute -top-3 -left-2 text-6xl text-[#B4A7D6]/40 font-serif leading-none select-none" aria-hidden="true" data-id="element-240">
                  &ldquo;
                </span>
                <p className="font-serif italic text-lg sm:text-xl lg:text-2xl leading-snug text-[#F5F0E8] text-center" data-id="element-241">
                  Tus pies son el{' '}
                  <span className="text-[#B4A7D6] font-semibold not-italic" data-id="element-242">
                    punto de descarga energética más poderoso
                  </span>{' '}
                  del cuerpo.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}